import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { AppShell } from '@/components/layout/AppShell';
import { PageContainer } from '@/components/layout/PageContainer';
import { TopBar } from '@/components/layout/TopBar';
import { Input } from '@/components/ui/Input';
import { Button } from '@/components/ui/Button';
import { Select } from '@/components/ui/Select';
import { Card } from '@/components/ui/Card';
import { useSearchFlights } from '@/api/queries/booking';
import { useUIStore } from '@/store/ui';
import { centsToUsdString } from '@/utils/money';
import { useLocale } from '@/hooks/useLocale';
import { formatDateTime, formatDurationCompact } from '@/utils/date';
import type { FlightOption, TicketClass } from '@/api/types';

export function TravelFlightSearchPage(): JSX.Element {
  const { t } = useTranslation();
  const { locale } = useLocale();
  const [from, setFrom] = useState('');
  const [to, setTo] = useState('');
  const [depart, setDepart] = useState('');
  const [pax, setPax] = useState(1);
  const [cls, setCls] = useState<TicketClass>('economy');
  const search = useSearchFlights();
  const pushToast = useUIStore((s) => s.pushToast);
  const [results, setResults] = useState<FlightOption[]>([]);

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!from || !to || !depart) {
      pushToast({ body: t('errors.validation'), variant: 'error' });
      return;
    }
    try {
      const r = await search.mutateAsync({
        from_iata: from.toUpperCase(),
        to_iata: to.toUpperCase(),
        depart_date: depart,
        passengers_count: pax,
        class: cls,
      });
      setResults(r);
    } catch {
      pushToast({ body: t('errors.generic'), variant: 'error' });
    }
  };

  return (
    <AppShell>
      <PageContainer>
        <TopBar back title={t('travel.flight_search.title')} />
        <form onSubmit={submit} style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
          <Input label={t('travel.flight_search.from')} value={from} onChange={(e) => setFrom(e.target.value)} maxLength={3} />
          <Input label={t('travel.flight_search.to')} value={to} onChange={(e) => setTo(e.target.value)} maxLength={3} />
          <Input label={t('travel.flight_search.departure')} type="date" value={depart} onChange={(e) => setDepart(e.target.value)} />
          <Input
            label={t('travel.flight_search.passengers')}
            type="number"
            min={1}
            max={9}
            value={pax}
            onChange={(e) => setPax(Number(e.target.value))}
          />
          <Select
            label={t('travel.flight_search.class')}
            value={cls}
            onChange={(e) => setCls(e.target.value as TicketClass)}
            options={[
              { value: 'economy', label: t('booking.class.economy') },
              { value: 'business', label: t('booking.class.business') },
              { value: 'premium', label: t('booking.class.premium') },
            ]}
          />
          <Button type="submit" block size="lg" variant="primary" loading={search.isPending}>
            {t('travel.flight_search.submit')}
          </Button>
        </form>

        <div style={{ display: 'flex', flexDirection: 'column', gap: 8, marginTop: 16 }}>
          {results.map((o) => (
            <Card key={o.id} variant="light" padding="md">
              <div style={{ fontWeight: 700 }}>{o.from_iata} → {o.to_iata}</div>
              <div style={{ color: 'var(--color-text-on-light-muted)', fontSize: 14, marginTop: 4 }}>
                {formatDateTime(o.depart_at, locale)} · {formatDurationCompact(o.duration_minutes, locale)}
              </div>
              <div style={{ fontWeight: 700, marginTop: 8 }}>
                {centsToUsdString(o.price_cents, locale)}
              </div>
            </Card>
          ))}
        </div>
      </PageContainer>
    </AppShell>
  );
}
